import React, { useState, useEffect } from 'react';
import { EyeLogo } from '@/components/EyeLogo';
import { useIsAuthenticated } from '@/hooks/auth';
import { secretAtom } from '@/owner/atoms/auth';
import { useAtomValue } from 'jotai';

export function WelcomePortal() {
  const [ownerMode, setOwnerMode] = useState(
    localStorage.getItem('sclabs_owner_mode') === 'true' || window.txConsts.isOwnerIp === true
  );
  const isFivemAuthed  = useIsAuthenticated();
  const ownerSecret    = useAtomValue(secretAtom);
  const isOwnerAuthed  = Boolean(ownerSecret);

  const handleRedirect = (path: string) => {
    if (path === '/fivem') {
      // If not logged into FiveM panel, send to login with redirect back
      window.location.href = isFivemAuthed ? '/fivem' : '/fivem/login?r=%2Ffivem';
    } else if (path === '/panel') {
      // If not logged into owner panel, send to panel login
      window.location.href = isOwnerAuthed ? '/panel' : '/panel/login';
    } else {
      window.location.href = path;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('owner') === 'true') {
      setOwnerMode(true);
      localStorage.setItem('sclabs_owner_mode', 'true');
      window.dispatchEvent(new Event('sclabs_owner_mode_change'));
    }
  }, []);

  const handleLogoDoubleClick = () => {
    const nextMode = !ownerMode;
    setOwnerMode(nextMode);
    localStorage.setItem('sclabs_owner_mode', nextMode ? 'true' : 'false');
    window.dispatchEvent(new Event('sclabs_owner_mode_change'));
  };

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen bg-[#06080c] text-slate-100 font-sans overflow-hidden select-none">
      {/* Dynamic Cyber Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none"></div>

      {/* Cyberpunk Scanlines */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] bg-[size:100%_4px,3px_100%]"></div>

      {/* Background Radial Glows */}
      <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-[#8b5cf6]/10 blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-96 h-96 rounded-full bg-[#00ffd1]/10 blur-[100px] pointer-events-none"></div>

      {/* Hub Brand Header */}
      <div className="relative z-10 flex flex-col items-center mb-12 animate-fade-in text-center px-4">
        <div 
          onDoubleClick={handleLogoDoubleClick}
          className="mb-4 transform scale-110 drop-shadow-[0_0_15px_rgba(0,255,209,0.3)] cursor-pointer hover:opacity-90 active:scale-95 transition-all"
          title="SC Labs Logo (Double click to toggle owner panel mode)"
        >
          <EyeLogo />
        </div>
        <h1 className="text-3xl md:text-5xl font-black uppercase tracking-wider bg-gradient-to-r from-[#00ffd1] via-[#a78bfa] to-[#8b5cf6] bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(167,139,250,0.2)]">
          Welcome to SCLabs Hub
        </h1>
        <p className="text-sm md:text-base text-slate-400 font-mono tracking-widest mt-2 uppercase">
          Select interface to authorize access
        </p>
      </div>

      {/* Cards Selection Container */}
      <div className={`relative z-10 grid grid-cols-1 ${ownerMode ? 'md:grid-cols-2 max-w-4xl' : 'max-w-md'} gap-6 w-full px-6 animate-slide-up`}>
        {/* Card 1: Owner Panel (Only shown in ownerMode) */}
        {ownerMode && (
          <div 
            onClick={() => handleRedirect('/panel')}
            className="group relative cursor-pointer flex flex-col justify-between bg-slate-950/40 hover:bg-slate-950/60 border border-white/5 hover:border-[#8b5cf6]/50 rounded-2xl p-8 backdrop-blur-md transition-all duration-300 transform hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(139,92,246,0.15)]"
          >
            {/* Inner Accent Glow Line */}
            <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#8b5cf6]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <div>
              <div className="flex justify-between items-center mb-6">
                <span className="text-3xl">🎛️</span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#8b5cf6] font-bold bg-[#8b5cf6]/10 px-2.5 py-1 rounded-md border border-[#8b5cf6]/20">
                  Owner Only
                </span>
              </div>
              <h2 className="text-xl font-bold tracking-tight text-white mb-2 group-hover:text-[#a78bfa] transition-colors">
                Owner Panel
              </h2>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <span className="text-xs font-mono text-[#a78bfa] tracking-wider uppercase group-hover:translate-x-1 transition-transform">
                Open Panel &rarr;
              </span>
              <div className="w-8 h-8 rounded-full bg-[#8b5cf6]/10 group-hover:bg-[#8b5cf6]/20 flex items-center justify-center text-[#a78bfa] transition-colors border border-[#8b5cf6]/10">
                ⚡
              </div>
            </div>
          </div>
        )}

        {/* Card 2: FiveM Panel */}
        <div 
          onClick={() => handleRedirect('/fivem')}
          className="group relative cursor-pointer flex flex-col justify-between bg-slate-950/40 hover:bg-slate-950/60 border border-white/5 hover:border-[#00ffd1]/50 rounded-2xl p-8 backdrop-blur-md transition-all duration-300 transform hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(0,255,209,0.15)]"
        >
          {/* Inner Accent Glow Line */}
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#00ffd1]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

          <div>
            <div className="flex justify-between items-center mb-6">
              <span className="text-3xl">🎮</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#00ffd1] font-bold bg-[#00ffd1]/10 px-2.5 py-1 rounded-md border border-[#00ffd1]/20">
                FiveM Panel
              </span>
            </div>
            <h2 className="text-xl font-bold tracking-tight text-white mb-2 group-hover:text-[#00ffd1] transition-colors">
              FiveM Server Control
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed font-sans">
              Monitor live server console, perform jailed SFTP file editing, view server metrics, and audit actions.
            </p>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <span className="text-xs font-mono text-[#00ffd1] tracking-wider uppercase group-hover:translate-x-1 transition-transform">
              Open Panel &rarr;
            </span>
            <div className="w-8 h-8 rounded-full bg-[#00ffd1]/10 group-hover:bg-[#00ffd1]/20 flex items-center justify-center text-[#00ffd1] transition-colors border border-[#00ffd1]/10">
              🛠️
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer Info */}
      <div className="relative z-10 mt-16 text-slate-600 font-mono text-[10px] uppercase tracking-widest">
        SCLabs Hub Panel &bull; Secure Connection
      </div>
    </div>
  );
}
